import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { api } from '../../api/client';
import { apiErrorText } from '../../api/errorText';
import {
  byProvider,
  byWhatIsInside,
  contentsLabel,
  episodesLabel,
  groupHeading,
  groupVersions,
  seasonsOnOffer,
  titlesOf,
  type VersionGroup,
} from '../../api/releaseContents';
import type { CatalogueEntry, DiscoveredRelease } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { formatSize } from './formatSize';
import RemoveVersionDialog from './RemoveVersionDialog';

/// One title of the catalogue, and the one decision it offers.
///
/// Either this server holds it and the button plays it, or it does not and the button goes
/// looking. What comes back from looking is every version an indexer found, because which one
/// is worth watching is a decision for whoever is watching - a 4K copy of forty gigabytes and a
/// 1080p one of eight are not the same choice on every line.
///
/// A title the server already holds can still be looked for: a second version is how somebody
/// gets the small copy for the phone beside the large one for the television. What is already
/// on the shelf is marked as such in the list, and can be taken off it again from there.
export default function CatalogueDialog({
  entry,
  onClose,
  onFetched,
  lookOnOpen = false,
}: {
  entry?: CatalogueEntry;
  onClose: () => void;
  onFetched: () => void;
  /// Starts looking for versions as soon as the dialog opens - for the item page, where the
  /// button that opened it already said that is what it would do.
  lookOnOpen?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [versions, setVersions] = useState<DiscoveredRelease[]>();
  const [looking, setLooking] = useState(false);
  const [fetching, setFetching] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [done, setDone] = useState<string>();
  const [removing, setRemoving] = useState<DiscoveredRelease>();
  const [removedAny, setRemovedAny] = useState(false);

  const canRemove = can('ManageLibraries');

  // The catalogue title the versions belong to, where the dialog was opened from one. Sent with
  // every add, so what arrives is filed under this title at once rather than under whatever its
  // file name makes it look like. An entry built from an item on the shelf has no such title.
  const linkedTitle = entry && entry.title.provider !== 'library'
    ? { provider: entry.title.provider, kind: entry.title.kind, id: entry.title.id }
    : undefined;

  useEffect(() => {
    if (entry && lookOnOpen) {
      void look();
    }
    // Once per title opened: looking again on every render would be a search per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.title.id, lookOnOpen]);

  if (!entry) {
    return null;
  }

  // Held, unless everything of it that the list showed as held was removed from here. Playing
  // a title whose last version just went would open a page for nothing.
  const held = Boolean(entry.libraryItemId)
    && !(removedAny && versions !== undefined && !versions.some((release) => release.isAdded));

  // What holds the whole programme, then each season by number, then everything whose name
  // said nothing about seasons - which is every version of every film.
  const groups = groupVersions(versions ?? []);

  // A film is one group and needs no headings: it is a list of versions and always was.
  const isPlainList = groups.length <= 1;

  const close = () => {
    setVersions(undefined);
    setProblem(undefined);
    setDone(undefined);
    setRemoving(undefined);
    setRemovedAny(false);
    onClose();
  };

  /// Whatever shows what is on the shelf asks again: the catalogue behind this, the item pages,
  /// the rows on the home page.
  const refreshShelf = () => {
    onFetched();
    void queryClient.invalidateQueries({ queryKey: ['items'] });
    void queryClient.invalidateQueries({ queryKey: ['discovery'] });
  };

  async function look() {
    if (!entry) {
      return;
    }

    setLooking(true);
    setProblem(undefined);

    try {
      const found = await api.discoverySearch(entry.title.title, entry.title.kind);

      // Only the titles that really are this one, and then season packs first: somebody who
      // opened a programme wants the programme, and the fourth episode of season two is not an
      // answer to that however sharp its picture is.
      setVersions(
        titlesOf(entry.title.title, entry.title.year, found)
          .flatMap((title) => title.versions)
          .sort(byWhatIsInside),
      );
    } catch (caught) {
      setProblem(apiErrorText(caught, t).message);
    } finally {
      setLooking(false);
    }
  }

  /// Every episode of one season, in one request. What could not be had is named rather than
  /// quietly missing: eleven episodes and a reason for the twelfth is the honest answer.
  const fetchSeason = async (group: VersionGroup) => {
    setFetching(group.key);
    setProblem(undefined);
    setDone(undefined);

    try {
      const added: string[] = [];
      const failed: string[] = [];

      // Grouped by where each posting came from, because a batch is one provider's work. In
      // practice that is one group; the loop is what keeps it true when it is not.
      for (const [providerId, releases] of byProvider(group.wholeSeason)) {
        const outcome = await api.addReleases(
          providerId,
          releases.map((release) => release.id),
          linkedTitle,
        );

        added.push(...outcome.added.map((release) => release.id));
        failed.push(...outcome.failures.map((failure) => failure.releaseId));
      }

      refreshShelf();

      if (failed.length === 0) {
        close();

        return;
      }

      // Some of it arrived. The dialog stays open saying which did not, because closing on a
      // half-fetched season is how somebody ends up with a gap they find weeks later.
      setDone(t('discover.season.partly', { added: added.length, failed: failed.length }));
    } catch (caught) {
      setProblem(apiErrorText(caught, t).message);
    } finally {
      setFetching(undefined);
    }
  };

  const fetchOne = async (release: DiscoveredRelease) => {
    setFetching(release.id);
    setProblem(undefined);

    try {
      await api.addRelease(release.providerId, release.id, linkedTitle);
      refreshShelf();
      close();
    } catch (caught) {
      setProblem(apiErrorText(caught, t).message);
    } finally {
      setFetching(undefined);
    }
  };

  /// Takes a version off the shelf, and leaves the dialog open on the list with that version
  /// fetchable again - which is usually the next thing somebody does.
  const remove = async (release: DiscoveredRelease) => {
    setFetching(release.id);
    setProblem(undefined);

    try {
      await api.removeRelease(release.providerId, release.id);

      setVersions((current) =>
        current?.map((version) => (version.id === release.id ? { ...version, isAdded: false } : version)),
      );
      setRemovedAny(true);
      refreshShelf();
    } catch (caught) {
      setProblem(apiErrorText(caught, t).message);
    } finally {
      setFetching(undefined);
      setRemoving(undefined);
    }
  };

  const row = (release: DiscoveredRelease) => (
    <VersionRow
      key={release.id}
      release={release}
      busy={fetching !== undefined}
      fetching={fetching === release.id}
      onFetch={() => void fetchOne(release)}
      onRemove={canRemove ? () => setRemoving(release) : undefined}
    />
  );

  return (
    <Dialog open onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>
        {entry.title.title}
        {entry.title.year !== undefined && ` (${entry.title.year})`}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {held ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {!lookOnOpen && (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  sx={{ flexGrow: 1 }}
                  onClick={() => {
                    close();
                    navigate(`/items/${entry.libraryItemId}`);
                  }}
                >
                  {t('discover.play')}
                </Button>
              )}

              {entry.canFetch && (
                <Button
                  variant="outlined"
                  startIcon={<LibraryAddIcon />}
                  sx={{ flexGrow: 1 }}
                  disabled={looking}
                  onClick={() => void look()}
                >
                  {t('discover.fetchAnother')}
                </Button>
              )}
            </Stack>
          ) : entry.canFetch ? (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={looking}
              onClick={() => void look()}
            >
              {t('discover.fetch')}
            </Button>
          ) : (
            // Nothing on this server can go and look. Said plainly rather than by a button
            // that does nothing.
            <Typography color="text.secondary">{t('discover.nothingCanFetch')}</Typography>
          )}

          {looking && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography color="text.secondary">{t('discover.looking')}</Typography>
            </Stack>
          )}

          {entry.title.overview && <Typography>{entry.title.overview}</Typography>}

          {problem && <Alert severity="error">{problem}</Alert>}

          {done && <Alert severity="warning">{done}</Alert>}

          {versions && (
            <>
              <Typography variant="h3">{t('discover.versions')}</Typography>

              {versions.length === 0 && (
                <Typography color="text.secondary">{t('discover.versionsNone')}</Typography>
              )}

              {/* What can be had at all, before the list of who has it. For a programme this
                  is the question - which seasons are out there - and reading it off twenty
                  release names is not something anybody should have to do. */}
              {seasonsOnOffer(versions, t) && (
                <Typography variant="body2" color="text.secondary">
                  {seasonsOnOffer(versions, t)}
                </Typography>
              )}

              {isPlainList ? (
                <List dense disablePadding>
                  {(groups[0]?.releases ?? []).map(row)}
                </List>
              ) : (
                /* A programme: what covers everything, then season one, season two, and what
                   said nothing. Sorted by season rather than by picture quality, because
                   somebody looking for season three is looking for a number. */
                groups.map((group) => (
                  <Accordion key={group.key} disableGutters variant="outlined">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        sx={{ alignItems: 'center', flexWrap: 'wrap', width: '100%', paddingInlineEnd: 1 }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>{groupHeading(group, t)}</Typography>

                        <Typography variant="body2" color="text.secondary">
                          {group.episodes.length > 0
                            ? t(
                                group.episodes.length === 1
                                  ? 'discover.season.episode'
                                  : 'discover.season.episodes',
                                { total: group.episodes.length, episodes: episodesLabel(group) },
                              )
                            : t(
                                group.releases.length === 1
                                  ? 'discover.versions.one'
                                  : 'discover.versions.count',
                                { total: group.releases.length },
                              )}
                        </Typography>

                        <Box sx={{ flexGrow: 1 }} />

                        {/* Only where the season was never posted whole. Where it was, the
                            pack is the first row inside and needs no button of its own. */}
                        {group.wholeSeason.length > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              fetching === group.key ? (
                                <CircularProgress size={14} />
                              ) : (
                                <DownloadIcon />
                              )
                            }
                            disabled={fetching !== undefined}
                            onClick={(event) => {
                              // The button sits in the header of a panel that opens when its
                              // header is clicked, and fetching a season is not asking to look
                              // inside it.
                              event.stopPropagation();
                              void fetchSeason(group);
                            }}
                          >
                            {t(
                              group.wholeSeason.length === 1
                                ? 'discover.season.fetchOne'
                                : 'discover.season.fetchAll',
                              { total: group.wholeSeason.length },
                            )}
                          </Button>
                        )}
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 0 }}>
                      <List dense disablePadding>
                        {group.releases.map(row)}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <RemoveVersionDialog
        name={removing?.name}
        detail={removing ? formatSize(removing.sizeInBytes) : undefined}
        busy={removing !== undefined && fetching === removing.id}
        onCancel={() => setRemoving(undefined)}
        onConfirm={() => removing && void remove(removing)}
      />
    </Dialog>
  );
}

/// One version, as a row: what it holds, what it is called, and what it is told apart by.
///
/// One that is already on the shelf is not something to fetch again. It says so, and offers
/// to take it off instead where the person may.
function VersionRow({
  release,
  busy,
  fetching,
  onFetch,
  onRemove,
}: {
  release: DiscoveredRelease;
  busy: boolean;
  fetching: boolean;
  onFetch: () => void;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();

  const text = (
    <ListItemText
      primary={
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {contentsLabel(release.contents, t) && (
            <Chip size="small" color="primary" label={contentsLabel(release.contents, t)} />
          )}
          {release.isAdded && (
            <Chip size="small" color="success" variant="outlined" label={t('discover.badge.inLibrary')} />
          )}
          <span style={{ overflowWrap: 'anywhere' }}>{release.name}</span>
        </Stack>
      }
      secondary={describe(release)}
    />
  );

  if (release.isAdded) {
    return (
      <ListItem
        disableGutters
        sx={{ px: 2 }}
        secondaryAction={
          onRemove &&
          (fetching ? (
            <CircularProgress size={18} />
          ) : (
            <IconButton
              edge="end"
              disabled={busy}
              aria-label={t('versions.remove.action')}
              onClick={onRemove}
            >
              <DeleteOutlineIcon />
            </IconButton>
          ))
        }
      >
        {text}
      </ListItem>
    );
  }

  return (
    <ListItemButton disabled={busy} onClick={onFetch}>
      {text}
      {fetching ? <CircularProgress size={18} /> : <DownloadIcon />}
    </ListItemButton>
  );
}

/// Height, size and languages: what one version is told from another by.
function describe(release: DiscoveredRelease): string {
  const parts: string[] = [];

  if (release.height) {
    parts.push(`${release.height}p`);
  }

  if (release.sizeInBytes > 0) {
    parts.push(`${Math.round(release.sizeInBytes / 1024 / 1024 / 1024)} GB`);
  }

  parts.push(...release.languages.map((language) => language.toUpperCase()));

  if (release.source) {
    parts.push(release.source);
  }

  return parts.join(' · ');
}
